import { createActionsHook, createHook, createStore, type StoreActionApi } from 'react-sweet-state';

import type { AIEmojiSuggestionSource } from '../types/AIEmojiSuggestionSource.ts';

export type AIEmojiSuggestionState = {
	aiSuggestedEmojiId: string | undefined;
	aifcSuggestionInFlight: boolean;
	aiEmojiSuggestionSource: AIEmojiSuggestionSource | undefined;
	aiEmojiSuggestionSessionId: number;
	hasPasteAutoTitleEmojiAppliedThisSession: boolean;
};

type Api = StoreActionApi<AIEmojiSuggestionState>;

const initialState: AIEmojiSuggestionState = {
	aiSuggestedEmojiId: undefined,
	aifcSuggestionInFlight: false,
	aiEmojiSuggestionSource: undefined,
	aiEmojiSuggestionSessionId: 0,
	hasPasteAutoTitleEmojiAppliedThisSession: false,
};

const actions = {
	setAISuggestedEmojiId:
		(aiSuggestedEmojiId: string | undefined) =>
		({ setState }: Api) => {
			setState({ aiSuggestedEmojiId });
		},

	setAifcSuggestionInFlight:
		(aifcSuggestionInFlight: boolean) =>
		({ setState }: Api) => {
			setState({ aifcSuggestionInFlight });
		},

	setAIEmojiSuggestionSource:
		(aiEmojiSuggestionSource: AIEmojiSuggestionSource | undefined) =>
		({ setState }: Api) => {
			setState({ aiEmojiSuggestionSource });
		},

	setHasPasteAutoTitleEmojiAppliedThisSession:
		(hasPasteAutoTitleEmojiAppliedThisSession: boolean) =>
		({ setState }: Api) => {
			setState({ hasPasteAutoTitleEmojiAppliedThisSession });
		},

	/**
	 * Atomically bumps the session id and clears the companion fields. Called on
	 * each new title-generating rising edge so each session starts clean. Always
	 * prefer this over bumping `aiEmojiSuggestionSessionId` directly so the
	 * session-reset contract cannot be broken.
	 */
	resetForNewSession:
		(nextSource: AIEmojiSuggestionSource | undefined) =>
		({ getState, setState }: Api) => {
			setState({
				aiEmojiSuggestionSessionId: getState().aiEmojiSuggestionSessionId + 1,
				aiSuggestedEmojiId: undefined,
				aifcSuggestionInFlight: false,
				hasPasteAutoTitleEmojiAppliedThisSession: false,
				aiEmojiSuggestionSource: nextSource,
			});
		},
};

export type AIEmojiSuggestionActions = typeof actions;

export const AIEmojiSuggestionStore = createStore({
	name: 'AIEmojiSuggestionStore',
	initialState,
	actions,
});

export const useAIEmojiSuggestion = createHook(AIEmojiSuggestionStore);
export const useAIEmojiSuggestionActions = createActionsHook(AIEmojiSuggestionStore);
